import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE_OPTIONS = [10, 50, 100];

/**
 * Reusable Pagination component.
 *
 * Props:
 *   totalItems   - total number of items in the dataset
 *   page         - current 0-based page index
 *   pageSize     - current rows per page (10 | 50 | 100)
 *   onPageChange(newPage)      - called when page index changes
 *   onPageSizeChange(newSize)  - called when rows-per-page changes
 *   noun         - optional label, e.g. "users" → "Showing 1–10 of 42 users"
 */
export function Pagination({ totalItems, page, pageSize, onPageChange, onPageSizeChange, noun = 'items' }) {
  if (totalItems <= 0) return null;

  const totalPages = Math.ceil(totalItems / pageSize);
  const start = page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, totalItems);

  // Don't render pagination if everything fits on one page and size is smallest
  if (totalItems <= PAGE_SIZE_OPTIONS[0] && pageSize === PAGE_SIZE_OPTIONS[0]) return null;

  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center',
      gap: '4px 12px',
      padding: '8px 12px', borderTop: '1px solid #e4e4e7', fontSize: 11, color: '#71717a',
      background: '#fafaf9', flexShrink: 0,
    }}>
      {/* Page size selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ fontSize: 10 }}>Rows:</span>
        {PAGE_SIZE_OPTIONS.map((size) => (
          <button
            key={size}
            onClick={() => { onPageSizeChange(size); onPageChange(0); }}
            style={{
              padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
              border: pageSize === size ? '1px solid #18181b' : '1px solid #e4e4e7',
              background: pageSize === size ? '#18181b' : '#fff',
              color: pageSize === size ? '#fff' : '#71717a',
              cursor: 'pointer', lineHeight: '16px',
            }}
          >
            {size}
          </button>
        ))}
      </div>

      {/* Prev / page indicator / next */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <button
          onClick={() => onPageChange(Math.max(0, page - 1))}
          disabled={page === 0}
          style={{
            padding: '2px 5px', borderRadius: 3, border: '1px solid #e4e4e7',
            background: page === 0 ? '#f4f4f5' : '#fff', cursor: page === 0 ? 'default' : 'pointer',
            color: page === 0 ? '#d4d4d8' : '#18181b', display: 'inline-flex', alignItems: 'center',
            lineHeight: 1,
          }}
        >
          <ChevronLeft size={12} />
        </button>
        <span style={{ minWidth: 36, textAlign: 'center', fontSize: 10, fontWeight: 600, color: '#18181b' }}>
          {page + 1} / {totalPages}
        </span>
        <button
          onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1}
          style={{
            padding: '2px 5px', borderRadius: 3, border: '1px solid #e4e4e7',
            background: page >= totalPages - 1 ? '#f4f4f5' : '#fff',
            cursor: page >= totalPages - 1 ? 'default' : 'pointer',
            color: page >= totalPages - 1 ? '#d4d4d8' : '#18181b',
            display: 'inline-flex', alignItems: 'center', lineHeight: 1,
          }}
        >
          <ChevronRight size={12} />
        </button>
      </div>

      {/* Item count */}
      <span style={{ fontSize: 10, color: '#a1a1aa' }}>
        {start}–{end} of {totalItems}
      </span>
    </div>
  );
}

/**
 * Helper hook for managing pagination state.
 *
 * Usage:
 *   const { page, pageSize, setPage, setPageSize, paginate } = usePagination();
 *   const visibleItems = paginate(allItems);
 */
export function usePaginationState(defaultSize = 10) {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(defaultSize);

  const paginate = React.useCallback((items) => {
    if (!Array.isArray(items)) return [];
    return items.slice(page * pageSize, (page + 1) * pageSize);
  }, [page, pageSize]);

  // Reset page when data changes significantly
  const resetPage = React.useCallback(() => setPage(0), []);

  return { page, pageSize, setPage, setPageSize, paginate, resetPage };
}
